import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useDateSort } from '../../../../hooks/useDateSort';

export type DailyCountCell = {
  rate: number;
  count: number;
};

export type DailyCountTableRow = {
  date: string;
  ranges: DailyCountCell[];
  /** 날짜 바로 오른쪽에 매출 컬럼을 넣고 싶을 때만 채운다 (showRevenue와 함께 사용) */
  revenue?: number;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);

const headerCellSx = {
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: '#fafafa',
  fontSize: '0.75rem',
  py: 0.75,
  px: 0.5,
};

const bodyCellSx = {
  borderBottom: '1px solid #e0e0e0',
  fontSize: '0.7rem',
  py: 0.75,
  px: 0.5,
  verticalAlign: 'top',
};

type Props = {
  /** 테이블 헤더 라벨 (탭별로 다름) */
  columnLabels?: string[];
  rows?: DailyCountTableRow[];
  /** 셀에서 강조할 값 (기본: rate가 위/굵게, count가 아래) */
  emphasize?: 'rate' | 'count';
  /** true면 날짜 바로 오른쪽에 매출 컬럼을 추가로 표시 (row.revenue 사용) */
  showRevenue?: boolean;
};

export default function RouletteContentDailyCountTable({
  columnLabels,
  rows,
  emphasize = 'rate',
  showRevenue = false,
}: Props) {
  const labels = columnLabels ?? [];
  const tableRows = rows ?? [];
  const { sorted, sortDirection, toggleSort } = useDateSort(tableRows, (r) => r.date, 'desc');

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: 3,
        p: 2,
        mt: 2,
        overflow: 'auto',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="#475569" sx={{ fontSize: '0.9rem' }}>
        상세 현황
      </Typography>
      <TableContainer sx={{ width: '100%' }}>
        <Table size="small" stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                onClick={toggleSort}
                sx={{
                  ...headerCellSx,
                  minWidth: 72,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#f1f5f9' },
                }}
              >
                날짜 {sortDirection === 'asc' ? '↑' : '↓'}
              </TableCell>
              {showRevenue && (
                <TableCell align="center" sx={{ ...headerCellSx, minWidth: 88 }}>
                  매출
                </TableCell>
              )}
              {labels.map((label, idx) => (
                <TableCell key={idx} align="center" sx={headerCellSx}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date}>
                <TableCell align="center" sx={bodyCellSx}>
                  {row.date}
                </TableCell>
                {showRevenue && (
                  <TableCell align="center" sx={{ ...bodyCellSx, fontWeight: 600 }}>
                    {formatCurrency(row.revenue ?? 0)}
                  </TableCell>
                )}
                {labels.map((_, i) => {
                  const cell = row.ranges[i] ?? { rate: 0, count: 0 };
                  const rateText = `${cell.rate}%`;
                  const countText = cell.count.toLocaleString('ko-KR');
                  const [primaryText, secondaryText] =
                    emphasize === 'count' ? [countText, rateText] : [rateText, countText];
                  return (
                    <TableCell key={i} align="center" sx={bodyCellSx}>
                      <Box sx={{ lineHeight: 1.3 }}>
                        <Box
                          component="span"
                          sx={{ display: 'block', fontSize: '0.75rem', fontWeight: 600 }}
                        >
                          {primaryText}
                        </Box>
                        <Box
                          component="span"
                          sx={{ display: 'block', fontSize: '0.68rem', color: '#64748b' }}
                        >
                          {secondaryText}
                        </Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
